/**
 * ───────────────────────────────────────────────────────────────────────────
 * CREDIT PACKS — single source of truth for the client.
 * ───────────────────────────────────────────────────────────────────────────
 *
 * A pack is a MULTI-CREDIT version of the $29 single lead credit. Nothing else
 * about it is different:
 *
 *   PACKS ADD CREDITS. THEY NEVER SPEND THEM.
 *   A credit leaves the balance in exactly one place — mkt_claim_lead_core,
 *   when the contractor presses Unlock Lead on a project they chose.
 *
 * Payment runs on the SAME Stripe connected account + payment gateway the $29
 * purchase already uses in production. No second processor exists.
 *
 * The server keeps its own copy of this table inside the `membership-credits`
 * edge function and is AUTHORITATIVE: the credits granted are derived from the
 * amount actually charged, so editing the numbers here can never inflate a
 * grant. Keep the two lists identical when prices change.
 */

import { LEAD_PRICE } from '@/data/constants';

export interface CreditPack {
  /** Code sent to the edge function. */
  code: 'pack3' | 'pack5' | 'pack10';
  credits: number;
  /** Charge in cents. */
  amount: number;
  /** Marketing flag — the middle pack is the default recommendation. */
  popular?: boolean;
}

export const CREDIT_PACKS: CreditPack[] = [
  { code: 'pack3', credits: 3, amount: 7900 },
  { code: 'pack5', credits: 5, amount: 12500, popular: true },
  { code: 'pack10', credits: 10, amount: 22900 },
];

export const packByCode = (code?: string | null): CreditPack | null =>
  CREDIT_PACKS.find((p) => p.code === code) || null;

/** Price of one credit inside a pack, in cents. */
export const packUnitPrice = (pack: CreditPack): number => Math.round(pack.amount / pack.credits);

/** What the same credits would cost one at a time at the $29 list price. */
export const packListPrice = (pack: CreditPack): number => pack.credits * LEAD_PRICE;

/** Cents saved versus buying the credits individually. */
export const packSavings = (pack: CreditPack): number => Math.max(0, packListPrice(pack) - pack.amount);

/** Whole-percent discount versus the $29 list price. */
export const packSavingsPct = (pack: CreditPack): number => {
  const list = packListPrice(pack);
  if (!list) return 0;
  return Math.round((packSavings(pack) / list) * 100);
};
